/**
 * Funções puras da sessão de voz longa (resumption + reconexão).
 *
 * Extraídas do hook `useVoiceSession` para ficarem testáveis sem React, áudio ou
 * o SDK `firebase/ai`: cálculo do backoff, máquina de decisão de término do loop
 * de `receive()`, rastreio do handle de resumption e formatação do tempo
 * decorrido. NÃO importar aqui nada de React/Web Audio/firebase.
 */

/** Motivo pelo qual o loop de `receive()` terminou. */
export type LoopEndReason = 'goingAway' | 'natural' | 'error'

/** Ação a tomar quando o loop de `receive()` termina. */
export type LoopDecision = 'reconnect' | 'stop' | 'error'

export interface LoopEndParams {
  /** Como o loop terminou: aviso de saída do servidor, fim natural ou exceção. */
  reason: LoopEndReason
  /** A sessão já está sendo encerrada pelo usuário/unmount. */
  stopped: boolean
  /** Há um handle de resumption guardado (a sessão pode ser retomada). */
  hasHandle: boolean
  /** Tentativas de reconexão já consumidas. */
  attempts: number
  /** Teto de tentativas de reconexão. */
  maxAttempts: number
}

/**
 * Atraso (ms) da próxima tentativa de reconexão: backoff exponencial
 * `baseDelayMs * 2**attempts`. `attempts` é o número de tentativas já feitas
 * (0 na primeira), então a progressão é base, 2·base, 4·base, 8·base, …
 */
export function reconnectDelayMs(attempts: number, baseDelayMs: number): number {
  return baseDelayMs * 2 ** attempts
}

/** True quando ainda há orçamento de tentativas de reconexão. */
export function canReconnect(attempts: number, maxAttempts: number): boolean {
  return attempts < maxAttempts
}

/**
 * Máquina de decisão do término do loop de `receive()`.
 *
 * - `stopped` (encerramento em curso) sempre vence → `stop` (nada a fazer).
 * - Havendo handle e orçamento de tentativas → `reconnect`, para qualquer motivo.
 * - Sem poder reconectar: uma queda por exceção (`error`) vira o fluxo de erro
 *   pt-BR; fim natural ou `goingAway` encerram com cortesia (`stop`), como antes.
 */
export function decideLoopEnd(params: LoopEndParams): LoopDecision {
  const { reason, stopped, hasHandle, attempts, maxAttempts } = params
  if (stopped) return 'stop'
  if (hasHandle && canReconnect(attempts, maxAttempts)) return 'reconnect'
  return reason === 'error' ? 'error' : 'stop'
}

/** Forma mínima do update de resumption emitido pelo `receive()`. */
export interface ResumptionUpdateLike {
  newHandle?: string
  resumable?: boolean
}

/**
 * Rastreia o handle de resumption. Só substitui o handle atual quando o update
 * é retomável e traz um `newHandle` não vazio; caso contrário preserva o handle
 * anterior (um update não-retomável não deve descartar um handle já válido).
 */
export function nextResumptionHandle(
  current: string | null,
  update: ResumptionUpdateLike,
): string | null {
  if (update.resumable && update.newHandle) return update.newHandle
  return current
}

/** Formata segundos decorridos como "m:ss" (ex.: 65 → "1:05"). */
export function formatElapsed(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}
